import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Smartphone,
  Monitor,
  Tablet,
  Shield,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../../src/hooks/useAuth';
import type { DeviceContext } from '../../lib/utils/contextAwareAuth';

/**
 * Smart Login Indicator Component
 * Provides contextual feedback about device recognition and trust status
 * Shows progressive security messages based on authentication state
 */

interface SmartLoginIndicatorProps {
  className?: string;
}

export const SmartLoginIndicator: React.FC<SmartLoginIndicatorProps> = ({
  className = ''
}) => {
  const { authState } = useAuth();
  const [deviceContext, setDeviceContext] = useState<DeviceContext | null>(null);
  const [trustStatus, setTrustStatus] = useState<'unknown' | 'trusted' | 'new' | 'expired'>('unknown');
  const [showIndicator, setShowIndicator] = useState(false);

  // Track whether the user was authenticated on mount (page reload vs fresh login)
  const [wasAuthOnMount] = useState(() => authState.isAuthenticated);

  useEffect(() => {
    // Get device context for smart authentication
    import('../../lib/utils/contextAwareAuth')
      .then(({ analyzeDeviceContext }) => {
        const context = analyzeDeviceContext();
        setDeviceContext(context);
      })
      .catch(() => {
        setDeviceContext({
          type: 'unknown',
          location: 'unknown',
          timeOfDay: 'unknown',
          riskLevel: 'medium',
          trustScore: 50,
        });
      });
  }, []);

  useEffect(() => {
    // Only show indicator on FRESH LOGIN (auth transitioning from false to true)
    // Skip on page reload where user was already authenticated
    if (authState.isAuthenticated && deviceContext && !wasAuthOnMount) {
      if (deviceContext.trustScore > 80) {
        setTrustStatus('trusted');
      } else if (deviceContext.riskLevel === 'high') {
        setTrustStatus('new');
      } else {
        setTrustStatus('trusted');
      }

      setShowIndicator(true);

      const timer = setTimeout(() => {
        setShowIndicator(false);
      }, 6000);

      return () => clearTimeout(timer);
    } else if (!authState.isAuthenticated) {
      setShowIndicator(false);
    }
  }, [authState.isAuthenticated, deviceContext, wasAuthOnMount]);

  const getDeviceIcon = () => {
    if (!deviceContext) return Monitor;

    // Use device context type to determine icon
    switch (deviceContext.type) {
      case 'work':
        return Monitor; // Work devices typically desktops/laptops
      case 'personal':
        return Smartphone; // Personal devices often mobile
      case 'public':
        return Tablet; // Public devices could be tablets/kiosks
      default:
        return Monitor;
    }
  };

  // Build a personalized greeting using the user's first name
  const userName = authState.user?.name?.split(' ')[0] || '';

  const getTrustStatusInfo = () => {
    switch (trustStatus) {
      case 'trusted':
        return {
          icon: CheckCircle,
          color: 'text-green-600 dark:text-green-400',
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-800',
          message: userName ? `Welcome back, ${userName}` : 'Welcome back',
          subMessage: 'Signed in securely',
          type: 'success'
        };
      case 'new':
        return {
          icon: AlertTriangle,
          color: 'text-amber-600 dark:text-amber-400',
          bgColor: 'bg-amber-50 dark:bg-amber-900/20',
          borderColor: 'border-amber-200 dark:border-amber-800',
          message: 'New device detected',
          subMessage: 'Extra verification for your security',
          type: 'warning'
        };
      default:
        return {
          icon: Shield,
          color: 'text-blue-600 dark:text-blue-400',
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800',
          message: userName ? `Hi, ${userName}` : 'Signed in',
          subMessage: 'Your account is secure',
          type: 'info'
        };
    }
  };

  const statusInfo = getTrustStatusInfo();
  const DeviceIcon = getDeviceIcon();
  const StatusIcon = statusInfo.icon;

  if (!showIndicator || !authState.isAuthenticated) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
          duration: 0.4
        }}
        className={`fixed top-4 right-4 z-50 max-w-md ${className}`}
      >
        <div className={`
          p-4 rounded-xl border shadow-lg backdrop-blur-sm
          ${statusInfo.bgColor} ${statusInfo.borderColor}
        `}>
          <div className="flex items-start space-x-3">
            {/* Device Icon */}
            <div className={`
              w-10 h-10 rounded-lg flex items-center justify-center
              ${statusInfo.type === 'success' ? 'bg-green-100 dark:bg-green-900' :
                statusInfo.type === 'warning' ? 'bg-amber-100 dark:bg-amber-900' :
                statusInfo.type === 'info' ? 'bg-blue-100 dark:bg-blue-900' :
                'bg-gray-100 dark:bg-gray-900'}
            `}>
              <DeviceIcon className={`w-5 h-5 ${statusInfo.color}`} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <StatusIcon className={`w-4 h-4 ${statusInfo.color}`} />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {statusInfo.message}
                  </span>
                </div>

                {/* Close button */}
                <motion.button
                  onClick={() => setShowIndicator(false)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-1 hover:bg-black/10 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              </div>

              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {statusInfo.subMessage}
              </p>
            </div>
          </div>

          {/* Progress bar for auto-hide */}
          <motion.div
            initial={{ width: '100%' }}
            animate={{ width: '0%' }}
            transition={{ duration: 5, ease: 'linear' }}
            className="h-0.5 bg-current opacity-20 rounded-full mt-3"
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Hook for using smart login indicators
 * Provides programmatic control over the indicator
 */
export const useSmartLoginIndicator = () => {
  const [isVisible, setIsVisible] = useState(false);

  const showIndicator = () => setIsVisible(true);
  const hideIndicator = () => setIsVisible(false);

  return {
    isVisible,
    showIndicator,
    hideIndicator,
    SmartLoginIndicator: (props: SmartLoginIndicatorProps) => (
      <SmartLoginIndicator {...props} />
    )
  };
};
